import React, { memo } from 'react';
import { Block } from '@/types/Document';
import { hasHtmlContent, renderHtmlContent } from '@/lib/document-utils';
import SignedHtml from './SignedHtml';
import ItemActionMenu from './ItemActionMenu';

interface BlockRendererProps {
  block: Block;
  blockKey: string;
  blockPath: string;
  flowIndex: number;
  phaseIndex: number;
  stepIndex: number;
  editMode: boolean;
  openMenuPath: string | null;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  handleDeleteItem: (itemPath: string) => void;
  handleAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
}

const BlockRenderer: React.FC<BlockRendererProps> = memo(({
  block,
  blockKey,
  blockPath,
  flowIndex,
  phaseIndex,
  stepIndex,
  editMode,
  openMenuPath,
  setOpenMenuPath,
  handleInputChange,
  handleDeleteItem,
  handleAddItem,
  handleChangeItemType,
  handleCreateStepFromSelection,
}) => {
  const menuPath = `block-${flowIndex}-${phaseIndex}-${stepIndex}-${blockKey}`;
  const isMenuOpen = openMenuPath === menuPath;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex justify-between items-start">
        <div className="flex-grow mr-3">
          {editMode ? (
            <textarea
              value={block?.text ?? ''}
              onChange={(e) => 
                handleInputChange(
                  [flowIndex, 'phases', phaseIndex, 'steps', stepIndex, 'blocks', blockKey, 'text'], 
                  e.target.value, 
                  'block'
                )
              }
              className="w-full min-h-[70px] whitespace-pre-wrap p-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
              rows={3}
            />
          ) : (
            <>
              {block?.text && hasHtmlContent(block.text) ? (
                <SignedHtml html={renderHtmlContent(block.text)} />
              ) : (
                <p className="whitespace-pre-wrap text-sm text-gray-800">
                  {block?.text ?? ''}
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex space-x-1 text-gray-400 relative flex-shrink-0">
          {editMode && (
            <button 
              type="button"
              onClick={() => setOpenMenuPath(isMenuOpen ? null : menuPath)}
              className="p-1 rounded hover:bg-gray-200"
              title="Block Actions"
              aria-label="Block actions menu"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth={2}
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" 
                />
              </svg>
            </button>
          )}
          {isMenuOpen && (
            <ItemActionMenu 
              itemPath={blockPath}
              itemType="block"
              onClose={() => setOpenMenuPath(null)}
              onAddItem={handleAddItem}
              onDeleteItem={handleDeleteItem}
              onChangeItemType={handleChangeItemType}
              onCreateStepFromSelection={handleCreateStepFromSelection}
            />
          )}
        </div>
      </div>
    </div>
  );
});

BlockRenderer.displayName = 'BlockRenderer';

export default BlockRenderer;
